import { PROMPTS_SOURCES } from '@/domain/messageEnums';
import type { V2Launcher } from '@/launchers/types';
import { activePlaceholders, usablePlaceholders } from '@/lib/activePlaceholders';
import { cleanGeneratedPrompts } from '@/lib/beacon/cleanGeneratedPrompts';
import type { PagePromptsAnswer } from '@/lib/beacon/pagePrompts';
import { findChatBarLauncher } from '@/lib/chatBar/findChatBarLauncher';
import { asRecord } from '@/lib/utils/isRecord';

// A generated prompt gets the same length limit the admin puts on an authored one.
const PLACEHOLDER_MAX_LENGTH = 128;

export type PlaceholderList = [string, ...string[]];

// Not V2Configuration: the V1-embed path hands over an unvalidated v2.
export interface ChatBarPlaceholderSource {
  appearance?: { input?: { placeholderTexts?: readonly unknown[] } };
  launchers?: readonly V2Launcher[];
}

export interface ChatBarPlaceholders {
  // The launcher whose answer a bar on the AI source reads; null for a static bar, which reads none.
  aiLauncherId: string | null;
  maxPlaceholders: number;
  staticPlaceholders: PlaceholderList;
}

export function resolveChatBarPlaceholders(v2: ChatBarPlaceholderSource | undefined): ChatBarPlaceholders {
  const legacy = v2?.appearance?.input?.placeholderTexts ?? [];
  const entry = findChatBarLauncher(v2?.launchers);
  const payload = asRecord(entry?.payload) ?? {};
  const maxPlaceholders = resolveMaxPlaceholders(payload.maxPlaceholders);
  // The admin card draws one box per slot up to the count, so text past it is text the admin never sees.
  const authored = usablePlaceholders(slotsWithin(payload.staticPlaceholders, maxPlaceholders));
  return {
    aiLauncherId: entry && payload.placeholdersSource === PROMPTS_SOURCES.AI ? entry.id : null,
    maxPlaceholders,
    staticPlaceholders: activePlaceholders(authored.length > 0 ? authored : slotsWithin(legacy, maxPlaceholders)),
  };
}

function slotsWithin(value: unknown, count: number): readonly unknown[] {
  return Array.isArray(value) ? value.slice(0, count) : [];
}

export interface DisplayedChatBarPlaceholders {
  placeholders: PlaceholderList;
  source: 'generated' | 'static';
}

// Pure, so both renderers agree on what the same answer displays.
export function displayedChatBarPlaceholders(
  { aiLauncherId, maxPlaceholders, staticPlaceholders }: ChatBarPlaceholders,
  answer: PagePromptsAnswer,
): DisplayedChatBarPlaceholders {
  if (aiLauncherId === null || answer.status !== 'ready') {
    return { placeholders: staticPlaceholders, source: 'static' };
  }
  const generated = cleanGeneratedPrompts(answer.prompts, {
    label: '[chatBar]',
    maxCount: maxPlaceholders,
    maxLength: PLACEHOLDER_MAX_LENGTH,
  });
  return generated
    ? { placeholders: [generated[0], ...generated.slice(1)], source: 'generated' }
    : { placeholders: staticPlaceholders, source: 'static' };
}

export function samePlaceholders(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((text, index) => text === b[index]);
}

function resolveMaxPlaceholders(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : Number.POSITIVE_INFINITY;
}
